import { Component, Input, OnInit, DestroyRef, inject } from '@angular/core';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import {
  UntypedFormControl,
  UntypedFormGroup,
  Validators,
  UntypedFormBuilder,
  UntypedFormArray,
  ReactiveFormsModule,
} from '@angular/forms';
import { NgxSpinnerService, NgxSpinnerModule } from 'ngx-spinner';
import { ProviderService } from '../../../../../@pages/provider/provider.service';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { InventoryService } from '../../../../../@pages/inventory/inventory.service';
import { PurchasesService } from '../../../../../@pages/purchases/purchases.service';
import { Observable } from 'rxjs';
import { IPurchasesInvoices } from '../../../../../@interfaces/purchaseInvoice.interface';
import { IProvider } from '../../../../../@interfaces/provider.interface';
import { IArticles } from '../../../../../@interfaces/article.interface';
import { IAlternativeCode } from '../../../../../@interfaces/alternative-code.interface';
import { startWith, map } from 'rxjs/operators';
import { RegisterPurchaseService } from './register-purchase.service';
import { ICities } from '../../../../../@interfaces/cities.interface';
import { TributaryService } from '../../../../../@pages/tributary/tributary.service';
import { ConverseFilesService } from '../../../services/files/converse-files.service';
import { IWarehouse } from 'src/app/@interfaces/warehouse.interface';
import { WarehouseService } from 'src/app/@pages/warehouse/warehouse.service';
import { MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { CookieAuthService } from 'src/app/@shared/storage-variables/cookie-auth.service';
import { IStorageCompany } from 'src/app/@interfaces/company.interface';
import { TaxValidationsService } from '../../../services/validations/tax-validations.service';
import { AlertService } from '@shared-services';
import { Router } from '@angular/router';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatOptionModule } from '@angular/material/core';
import { AsyncPipe, CurrencyPipe, DecimalPipe } from '@angular/common';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { STRING_CONSTANTS } from '@shared-constants';
import { UnitConversionService } from '../../../../services/unit-conversion.service';
import { IUnits } from 'src/app/@interfaces/units.interface';
import { MatSelectModule } from '@angular/material/select';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { DianSupportingDocumentService } from '@shared-services';
import { DianPurchase } from '@shared-models';
import { ValidatorsService } from 'src/app/@shared/validator/validators.service';
import { InventoryArticleWarningComponent } from '@shared-components';

@Component({
  selector: 'app-register-purchase',
  templateUrl: './register-purchase.component.html',
  styleUrls: ['./register-purchase.component.css'],
  standalone: true,
  imports: [
    MatDialogModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatAutocompleteModule,
    MatOptionModule,
    MatDatepickerModule,
    MatCheckboxModule,
    MatButtonModule,
    MatSelectModule,
    NgxSpinnerModule,
    MatProgressSpinnerModule,
    AsyncPipe,
    CurrencyPipe,
    DecimalPipe,
    TranslatePipe,
    InventoryArticleWarningComponent,
  ],
})
export class RegisterPurchaseComponent implements OnInit {
  // Inject services

  private spinnerService = inject(NgxSpinnerService);
  private providersService = inject(ProviderService);
  private article = inject(InventoryService);
  public dialog = inject(MatDialog);
  public purchasesService = inject(PurchasesService);
  private registerPurchaseService = inject(RegisterPurchaseService);
  private fb = inject(UntypedFormBuilder);
  private tributaryService = inject(TributaryService);
  private converseFilesService = inject(ConverseFilesService);
  private taxValidationsService = inject(TaxValidationsService);
  private warehouse = inject(WarehouseService);
  private readonly alertService = inject(AlertService);
  private readonly translate = inject(TranslateService);
  private readonly router = inject(Router);
  private cookieAuthService = inject(CookieAuthService);
  private unitConversionService = inject(UnitConversionService);
  private dianSupportingDocumentService = inject(DianSupportingDocumentService);
  private destroyRef = inject(DestroyRef);
  private validatorsService = inject(ValidatorsService);

  // Inputs
  @Input() uvtValue = 0;
  @Input() XMLFIle = '';
  @Input() totalPurchase = 0;
  @Input() totalWithoutTax = 0;
  @Input() totalTax = 0;
  @Input() withholdingTax = 0;
  @Input() icaTax = 0;
  loadingWithholdings = false;
  @Input() customerCity = '';
  @Input() providerCity = '';
  @Input() newProvider: UntypedFormControl = this.fb.control(
    '',
    Validators.required,
  );
  @Input() purchaseForm: UntypedFormGroup = this.fb.group({
    itemRows: this.fb.array([this.buildItemRow()]),
    written_invoice: ['', Validators.required],
    tax_include: false,
    clause: false,
    is_electronic_document: false,
    is_draft: false,
    tax: [0, [Validators.maxLength(2), Validators.min(0)]],
    date: [new Date(), Validators.required],
    dueDate: [new Date(), Validators.required],
  });

  // State
  providersList: IProvider[] = [];
  articleList: IArticles[] = [];
  articles: IArticles[] = [];
  providers: IProvider[] = [];
  setPurchaseInvoice = {} as IPurchasesInvoices;
  warehouseList: IWarehouse[] = [];
  unitsList: IUnits[] = [];
  citiesList: ICities[] = [];
  companyObject = {} as IStorageCompany;
  citiesCustomerForm: UntypedFormControl = this.fb.control(
    '',
    Validators.required,
  );
  filteredOptionsCustomerCities: Observable<ICities[]> | undefined;
  currentWarehouse = 0;
  companyId = '';
  user = '';
  rol = '';

  // Accessors
  get rowsArr(): UntypedFormArray {
    return this.purchaseForm.get('itemRows') as UntypedFormArray;
  }

  get isElectronicInvoicing(): boolean {
    return (
      this.companyObject.internal_electronic_invoicing === STRING_CONSTANTS.TRUE
    );
  }

  private get isColombianCompany(): boolean {
    return this.companyObject.countries[0].name === 'Colombia';
  }

  // Lifecycle
  async ngOnInit(): Promise<void> {
    this.getAuthValues();
    this.getInputValues();
    this.citiesList = await this.getCities();
    this.setCitiesForm(this.citiesList);
    this.filteredArrays();
    this.loadData();
  }

  // Init helpers
  private getAuthValues(): void {
    this.companyObject = this.cookieAuthService.getCompanyObject!;
    this.companyId = this.companyObject.Id_company!.toString();
    this.user = this.cookieAuthService.getUserId!;
    this.rol = this.cookieAuthService.getRolId!;
  }

  private loadData(): void {
    this.warehouse
      .listWarehouses()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (result) => (this.warehouseList = result),
      });

    this.article
      .listUnits()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (result) => (this.unitsList = result || []),
      });
  }

  private getCities(): Promise<ICities[]> {
    const country_id = this.companyObject.countries[0].id_country;
    return new Promise((resolve) => {
      this.tributaryService
        .getCitiesByCompany(parseInt(country_id!.toString()))
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: (result) => resolve(result),
        });
    });
  }

  private filteredArrays(): void {
    this.filteredOptionsCustomerCities =
      this.citiesCustomerForm.valueChanges.pipe(
        startWith(''),
        map((value) => this._filterC(value)),
      );
  }

  private getInputValues(): void {
    this.newProvider.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (value) => {
          if (!value.__typename) {
            this.providersService
              .searchProvider(value, null, null)
              .pipe(takeUntilDestroyed(this.destroyRef))
              .subscribe({
                next: (result) => (this.providersList = result.providers),
              });
          }
        },
      });

    this.rowsArr.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (value: any[]) => {
          value.forEach((element, index) => {
            if (element.article && this.isSearchableValue(element.article)) {
              // Store the searched value for later matching with alternative codes
              const row = this.rowsArr.at(index);
              row.patchValue(
                { searchedAlternativeCode: element.article },
                { emitEvent: false },
              );

              // Search by alternative code
              this.article
                .searchArticleByProviderCodes(element.article)
                .pipe(takeUntilDestroyed(this.destroyRef))
                .subscribe({
                  next: (alternativeResults: IArticles[]) => {
                    // Also search by normal article code
                    this.article
                      .searchArticle(
                        element.article,
                        parseInt(this.currentWarehouse.toString()),
                        null,
                        null,
                      )
                      .pipe(takeUntilDestroyed(this.destroyRef))
                      .subscribe({
                        next: (result: any) => {
                          // Combine results, avoiding duplicates
                          const normalResults = result.articles || [];
                          const combined = [...alternativeResults];
                          normalResults.forEach((article: IArticles) => {
                            if (
                              !combined.find(
                                (a) => a.id_articulo === article.id_articulo,
                              )
                            ) {
                              combined.push(article);
                            }
                          });
                          this.articleList = combined;
                        },
                        error: () => {
                          // On error, use only alternative code results
                          this.articleList = alternativeResults;
                        },
                      });
                  },
                  error: () => {
                    // If alternative code search fails, try normal search
                    this.article
                      .searchArticle(
                        element.article,
                        parseInt(this.currentWarehouse.toString()),
                        null,
                        null,
                      )
                      .pipe(takeUntilDestroyed(this.destroyRef))
                      .subscribe({
                        next: (result: any) =>
                          (this.articleList = result.articles),
                      });
                  },
                });
            }
          });
        },
      });
  }

  // Validators
  private applyDraftValidators(isDraft: boolean): void {
    const formFields = ['written_invoice', 'date', 'dueDate'];

    // Customer city
    if (isDraft) {
      this.citiesCustomerForm.clearValidators();
    } else {
      this.citiesCustomerForm.setValidators(Validators.required);
    }
    this.citiesCustomerForm.updateValueAndValidity({ emitEvent: false });

    // Form fields
    formFields.forEach((field) => {
      if (field !== 'written_invoice') {
        const control = this.purchaseForm.get(field);
        isDraft
          ? control?.clearValidators()
          : control?.setValidators(Validators.required);
        control?.updateValueAndValidity({ emitEvent: false });
      }
    });

    // Row fields
    this.rowsArr.controls.forEach((row) => {
      const rowFields = [
        'warehouse',
        'article',
        'unit',
        'discount',
        'subtotal',
      ];
      rowFields.forEach((field) => {
        const control = row.get(field);
        isDraft
          ? control?.clearValidators()
          : control?.setValidators(Validators.required);
        control?.updateValueAndValidity({ emitEvent: false });
      });

      if (isDraft) {
        row.get('quantity')?.clearValidators();
        row.get('price')?.clearValidators();
      } else {
        row
          .get('quantity')
          ?.setValidators([
            Validators.required,
            Validators.min(0.001),
            this.validatorsService.positiveDecimal,
          ]);
        row
          .get('price')
          ?.setValidators([Validators.required, Validators.min(0.001)]);
      }
      row.get('quantity')?.updateValueAndValidity({ emitEvent: false });
      row.get('price')?.updateValueAndValidity({ emitEvent: false });
    });
  }

  onDraftChange(event: any): void {
    this.applyDraftValidators(event.checked);
  }

  // Form helpers
  private buildItemRow(): UntypedFormGroup {
    return this.fb.group({
      warehouse: ['', Validators.required],
      article: ['', Validators.required],
      quantity: [
        null,
        [
          Validators.required,
          Validators.min(0.001),
          this.validatorsService.positiveDecimal,
        ],
      ],
      unit: ['', Validators.required],
      customFactor: [1],
      isFactorEditable: [false],
      quantityInBaseUnit: [0],
      price: [null, [Validators.required, Validators.min(0.001)]],
      discount: [0, Validators.required],
      subtotal: [0, Validators.required],
      searchedAlternativeCode: [''], // Stores the searched value to match against alternative codes
    });
  }

  // Cities
  onCitiesCustomer(event: MatAutocompleteSelectedEvent): void {
    this.customerCity = event.option.value.name;
    this.setCitiesForm(this.citiesList);
    this.calculation();
  }

  setCitiesForm(citiesList: ICities[]): void {
    if (!this.customerCity) return;
    const normalizedCustomer = this.converseFilesService.removeAccents(
      this.customerCity.toString().toLocaleLowerCase().trim(),
    );
    const match = citiesList.find(
      (city) =>
        this.converseFilesService.removeAccents(
          city.name.toString().toLocaleLowerCase(),
        ) === normalizedCustomer,
    );
    if (match) this.citiesCustomerForm.setValue(match);
  }

  // Provider
  onProviderSelected(event: MatAutocompleteSelectedEvent): void {
    if (event.option.value) {
      this.providerCity = event.option.value.city;
      this.calculation();
    }
  }

  // Rows
  updateCurrentWareouse(id: number): void {
    this.currentWarehouse = id;
  }

  addNewRow(): void {
    const isDraft = this.purchaseForm.get('is_draft')?.value;

    if (!isDraft && this.purchaseForm.invalid) return;

    this.rowsArr.push(
      this.fb.group({
        warehouse: ['', Validators.required],
        article: ['', Validators.required],
        quantity: [
          '0',
          [
            Validators.required,
            Validators.min(0.001),
            this.validatorsService.positiveDecimal,
          ],
        ],
        unit: ['', Validators.required],
        customFactor: [1],
        isFactorEditable: [false],
        quantityInBaseUnit: [0],
        price: [0, Validators.required],
        discount: [0, Validators.required],
        subtotal: [0, Validators.required],
      }),
    );

    if (isDraft) this.applyDraftValidators(true);

    this.calculation();
  }

  deleteRow(rowIndex: number): void {
    if (this.rowsArr.length > 1) {
      this.rowsArr.removeAt(rowIndex);
    } else {
      this.alertService.open({
        title: this.translate.instant('global.alerts.title.warning'),
        message: this.translate.instant(
          'purchases.register-purchase.remove-last-row',
        ),
        type: 'warning',
        acceptText: this.translate.instant('global.alerts.buttons.ok'),
      });
    }
    this.calculation();
  }

  // Validations
  private validRegister(): boolean {
    const isDraft = this.purchaseForm.get('is_draft')?.value;

    if (!isDraft && this.purchaseForm.invalid) {
      this.alertService.open({
        title: this.translate.instant('global.alerts.title.error'),
        message: this.translate.instant(
          'purchases.register-purchase.required-fields',
        ),
        type: 'error',
        acceptText: this.translate.instant('global.alerts.buttons.ok'),
      });
      this.spinnerService.hide();
      return false;
    }

    if (this.rowsArr.length > 12) {
      this.alertService.open({
        title: this.translate.instant('global.alerts.title.error'),
        message: this.translate.instant(
          'purchases.register-purchase.max-items-exceeded',
          { count: this.rowsArr.length - 12 },
        ),
        type: 'error',
        acceptText: this.translate.instant('global.alerts.buttons.ok'),
      });
      this.spinnerService.hide();
      return false;
    }

    return true;
  }

  private validProvider(variables: IPurchasesInvoices): boolean {
    if (!variables.provider.id_provider) {
      this.alertService.open({
        title: this.translate.instant('global.alerts.title.error'),
        message: this.translate.instant(
          'purchases.register-purchase.provider-not-found',
        ),
        type: 'error',
        acceptText: this.translate.instant('global.alerts.buttons.ok'),
      });
      this.spinnerService.hide();
      return false;
    }
    return true;
  }

  private validArticle(variables: IPurchasesInvoices): boolean {
    if (!variables.purchases[0].codigo) {
      this.alertService.open({
        title: this.translate.instant('global.alerts.title.error'),
        message: this.translate.instant(
          'purchases.register-purchase.article-not-found',
        ),
        type: 'error',
        acceptText: this.translate.instant('global.alerts.buttons.ok'),
      });
      this.spinnerService.hide();
      return false;
    }
    return true;
  }

  // Register
  register(): void {
    if (!this.validRegister()) return;

    this.calculation();

    const isDraft = this.purchaseForm.get('is_draft')?.value;

    if (
      !isDraft &&
      this.citiesCustomerForm.invalid &&
      this.isElectronicInvoicing
    ) {
      this.alertService.open({
        title: this.translate.instant('global.alerts.title.error'),
        message: this.translate.instant(
          'purchases.register-purchase.city-not-found',
        ),
        type: 'error',
        acceptText: this.translate.instant('global.alerts.buttons.ok'),
      });
      this.spinnerService.hide();
      return;
    }

    this.spinnerService.show();
    const variables = this.registerPurchaseService.setQuotationData(
      this.purchaseForm,
      this.rowsArr,
      this.newProvider,
    );

    if (!isDraft) {
      if (!this.validProvider(variables!) || !this.validArticle(variables!))
        return;
    }

    const p = variables.purchases[0];
    this.purchasesService
      .createPurchase(
        variables.contpurchase!.written_invoice,
        variables.is_electronic_document,
        variables.provider.id_provider!,
        variables.tax,
        this.withholdingTax,
        this.icaTax,
        this.customerCity,
        this.providerCity,
        p.id_warehouse,
        p.codigo,
        p.quantity,
        p.price,
        p.discount,
        p.subtotal,
        p.total,
        variables.createdAt,
        variables.dueDate,
        variables.tax_incl!,
        variables.clause,
        this.XMLFIle,
        parseInt(this.user!),
        p.id_purchase_unit,
        p.quantity_in_base_unit,
        variables.is_draft,
      )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: async (result) => {
          await this.onCreateSuccess(result, isDraft);
        },
        error: (error) => {
          this.spinnerService.hide();
          const isPurchaseExist = error?.graphQLErrors?.some(
            (e: { extensions?: { code?: string } }) =>
              e.extensions?.code === 'PURCHASE_ALREADY_EXISTS',
          );
          if (isPurchaseExist) {
            this.alertService.open({
              title: this.translate.instant('global.alerts.title.error'),
              message: this.translate.instant(
                'purchases.register-purchase.already-exists',
              ),
              type: 'error',
              acceptText: this.translate.instant('global.alerts.buttons.ok'),
            });
          }
        },
      });
  }

  async onCreateSuccess(result: DianPurchase, isDraft: boolean) {
    this.spinnerService.hide();
    if (
      this.purchaseForm.value.is_electronic_document &&
      this.companyObject.internal_electronic_invoicing ===
        STRING_CONSTANTS.TRUE &&
      !isDraft
    ) {
      await this.dianSupportingDocumentService.sendDian(
        parseInt(result.id_invoice!),
      );
    }
    this.alertService
      .open({
        title: this.translate.instant('global.alerts.title.success'),
        message: this.translate.instant(
          'purchases.register-purchase.create-success',
        ),
        type: 'success',
        acceptText: this.translate.instant('global.alerts.buttons.ok'),
      })
      .subscribe({
        accept: () =>
          this.router
            .navigateByUrl('/card', { skipLocationChange: true })
            .then(() => this.router.navigate(['/purchases'])),
      });
  }

  // Calculation
  async calculation(): Promise<void> {
    if (!this.newProvider.value?.contributorType) return;

    const contributorID = parseInt(
      this.newProvider.value.contributorType.id_contributor,
    );
    const taxIncluded = this.purchaseForm.get('tax_include')?.value === true;
    let totalTax = 0;
    let totalWithoutTax = 0;

    for (let i = 0; i < this.rowsArr.length; i++) {
      const row = this.rowsArr.value[i];
      const articleTax = row.article?.tax;

      if (!articleTax || !Array.isArray(articleTax) || articleTax.length === 0)
        continue;

      const taxValue = articleTax[0].value;
      const taxCal = taxIncluded
        ? 0
        : this.taxValidationsService.getContributorTax(
            contributorID,
            taxValue === 0 ? this.purchaseForm.value.tax : taxValue,
          );

      const gross = row.quantity * row.price;
      const discountAmount =
        row.discount !== 0 ? (row.discount / 100) * gross : 0;
      const subtotalBeforeTax = gross - discountAmount;
      const computedTax = taxCal
        ? taxValue === 0
          ? (taxCal / 100) * subtotalBeforeTax
          : taxCal * subtotalBeforeTax
        : 0;

      this.rowsArr.value[i].subtotal = subtotalBeforeTax + computedTax;
      totalTax += computedTax;
      totalWithoutTax += subtotalBeforeTax;
    }

    this.totalPurchase = this.rowsArr.value.reduce(
      (sum: number, row: any) => sum + row.subtotal,
      0,
    );
    this.totalTax = totalTax;
    this.totalWithoutTax = this.totalPurchase - this.totalTax;

    if (this.isColombianCompany) await this.calculateTaxes();
  }

  async calculateTaxes(): Promise<void> {
    this.loadingWithholdings = true;
    try {
      const result =
        await this.registerPurchaseService.calculateTaxesFromBackend(
          this.rowsArr,
          this.newProvider,
          this.customerCity,
          this.providerCity,
          this.purchaseForm.value.clause,
        );
      this.withholdingTax = result.withholdingTax;
      this.icaTax = result.icaTax;
    } finally {
      this.loadingWithholdings = false;
    }
    this.setCitiesForm(this.citiesList);
  }

  // Article / Unit
  async onArticleChange(rowIndex: number): Promise<void> {
    await this.onArticleSelected(rowIndex);
    await this.calculation();
  }

  async onArticleSelected(rowIndex: number): Promise<void> {
    const row = this.rowsArr.at(rowIndex);
    const article = row.get('article')?.value;
    if (!article) return;

    // Clear cache for this article to force recalculation
    if (article.id_articulo) {
      this.groupedUnitsCache.delete(article.id_articulo);
    }

    // Check if article was searched by alternative code
    const searchedValue = row.get('searchedAlternativeCode')?.value;
    const matchedAlternativeCode =
      searchedValue && article.alternativeCodes
        ? article.alternativeCodes.find(
            (altCode: IAlternativeCode) =>
              altCode.alternative_code?.toLowerCase().trim() ===
              searchedValue.toLowerCase().trim(),
          )
        : null;

    // If found a matching alternative code, use its unit and factor
    if (matchedAlternativeCode && matchedAlternativeCode.id_unit) {
      const alternativeFactor = matchedAlternativeCode.conversion_factor || 1;
      row.patchValue(
        {
          unit: matchedAlternativeCode.id_unit,
          customFactor: alternativeFactor,
          isFactorEditable: false,
          quantityInBaseUnit: 0,
        },
        { emitEvent: false },
      );
      return;
    }

    // Otherwise, use the default article unit and factor
    const hasPackagingUnit = !!article.id_packaging_unit;
    const defaultUnit = article.id_packaging_unit || article.unit?.id_unit;
    const defaultFactor = hasPackagingUnit
      ? article.packaging_unit_factor || 1
      : 1;

    if (defaultUnit) {
      row.patchValue(
        {
          unit: defaultUnit,
          customFactor: defaultFactor,
          isFactorEditable: false,
          quantityInBaseUnit: 0,
        },
        { emitEvent: false },
      );
    }
  }

  async onUnitOrQuantityChange(
    rowIndex: number,
    changedField?: 'unit' | 'quantity' | 'factor',
  ): Promise<void> {
    const row = this.rowsArr.at(rowIndex);
    const article = row.get('article')?.value;
    const quantity = Number(row.get('quantity')?.value) || 0;
    const selectedUnit = Number(row.get('unit')?.value);
    const baseUnit = article?.unit?.id_unit
      ? Number(article.unit.id_unit)
      : article?.id_unit;

    if (!article || !selectedUnit || !baseUnit) return;

    let factorToUse = parseFloat(row.get('customFactor')?.value) || 1;
    let isEditable = false;
    const shouldUpdateFactor = changedField === 'unit';

    if (shouldUpdateFactor) {
      if (selectedUnit === baseUnit) {
        factorToUse = 1;
      } else if (
        article.id_packaging_unit &&
        selectedUnit === Number(article.id_packaging_unit)
      ) {
        // Use packaging unit factor from article master data
        factorToUse = article.packaging_unit_factor || 1;
      } else {
        // Check if selected unit is from an alternative code
        const matchingAltCode = article.alternativeCodes?.find(
          (altCode: IAlternativeCode) =>
            Number(altCode.id_unit) === selectedUnit,
        );

        if (matchingAltCode && matchingAltCode.conversion_factor) {
          // Use alternative code's configured factor (non-editable)
          factorToUse = matchingAltCode.conversion_factor;
        } else {
          // Try conversion service for other units
          try {
            const factor = await this.unitConversionService
              .getConversionFactor(selectedUnit, baseUnit)
              .toPromise();
            factorToUse = factor ?? 1;
            isEditable = factor === null || factor === undefined;
          } catch {
            factorToUse = 1;
            isEditable = true;
          }
        }
      }
    }

    const patch = shouldUpdateFactor
      ? {
          quantityInBaseUnit: quantity * factorToUse,
          customFactor: factorToUse,
          isFactorEditable: isEditable,
        }
      : { quantityInBaseUnit: quantity * factorToUse };

    row.patchValue(patch, { emitEvent: false });
    await this.calculation();
  }

  getAvailableUnits(article: IArticles | null | undefined): IUnits[] {
    if (!article || !this.unitsList || this.unitsList.length === 0) {
      return [];
    }

    // Collect packaging unit IDs: base packaging unit + alternative codes units
    const packagingUnitIds = new Set<number>();

    // Add base packaging unit if exists
    if (article.id_packaging_unit) {
      packagingUnitIds.add(article.id_packaging_unit);
    }

    // Add alternative codes units safely
    if (article.alternativeCodes && Array.isArray(article.alternativeCodes)) {
      article.alternativeCodes.forEach((altCode) => {
        if (altCode?.id_unit) {
          packagingUnitIds.add(altCode.id_unit);
        }
      });
    }

    // Sort units: packaging units first, then others
    return this.unitsList.slice().sort((a, b) => {
      if (!a || !b) return 0;
      const aIsPackaging = packagingUnitIds.has(a.id_unit);
      const bIsPackaging = packagingUnitIds.has(b.id_unit);

      if (aIsPackaging && !bIsPackaging) return -1;
      if (!aIsPackaging && bIsPackaging) return 1;
      return 0; // Keep original order for units in same category
    });
  }

  isPackagingUnit(
    article: IArticles | null | undefined,
    unitId: number,
  ): boolean {
    if (!article || !unitId) return false;

    // Check if it's the base packaging unit
    if (article.id_packaging_unit === unitId) return true;

    // Check if it's from alternative codes
    if (article.alternativeCodes && Array.isArray(article.alternativeCodes)) {
      return article.alternativeCodes.some(
        (altCode) => altCode?.id_unit === unitId,
      );
    }

    return false;
  }

  private groupedUnitsCache = new Map<
    number,
    {
      packagingUnits: Array<{ unit: IUnits; code?: string; factor?: number }>;
      otherUnits: IUnits[];
    }
  >();

  getGroupedUnits(article: IArticles | null | undefined): {
    packagingUnits: Array<{ unit: IUnits; code?: string; factor?: number }>;
    otherUnits: IUnits[];
  } {
    // Return empty arrays if article or unitsList is not available
    if (!article || !this.unitsList || this.unitsList.length === 0) {
      return { packagingUnits: [], otherUnits: [] };
    }

    // Check cache first
    const articleId = article.id_articulo ? Number(article.id_articulo) : null;
    if (articleId && this.groupedUnitsCache.has(articleId)) {
      return this.groupedUnitsCache.get(articleId)!;
    }

    // Create map of packaging units with their alternative code and factor
    const packagingUnitsMap = new Map<
      number,
      { code?: string; factor?: number }
    >();

    // Add base packaging unit if exists (convert to number to ensure proper comparison)
    if (article.id_packaging_unit) {
      const packageUnitId = Number(article.id_packaging_unit);
      packagingUnitsMap.set(packageUnitId, {
        code: undefined, // Base unit doesn't have an alternative code
        factor: article.packaging_unit_factor || 1,
      });
    }

    // Add alternative codes units with their codes and factors
    if (article.alternativeCodes && Array.isArray(article.alternativeCodes)) {
      article.alternativeCodes.forEach((altCode) => {
        if (altCode?.id_unit) {
          const altUnitId = Number(altCode.id_unit);
          packagingUnitsMap.set(altUnitId, {
            code: altCode.alternative_code,
            factor: altCode.conversion_factor || 1,
          });
        }
      });
    }

    // If no packaging units found, return all units as "other"
    if (packagingUnitsMap.size === 0) {
      const result = { packagingUnits: [], otherUnits: [...this.unitsList] };
      if (articleId) this.groupedUnitsCache.set(articleId, result);
      return result;
    }

    // Separate units into packaging and others
    const packagingUnits: Array<{
      unit: IUnits;
      code?: string;
      factor?: number;
    }> = [];
    const otherUnits: IUnits[] = [];

    this.unitsList.forEach((unit) => {
      if (unit && unit.id_unit) {
        const unitId = Number(unit.id_unit);
        if (packagingUnitsMap.has(unitId)) {
          const unitInfo = packagingUnitsMap.get(unitId)!;
          packagingUnits.push({
            unit: unit,
            code: unitInfo.code,
            factor: unitInfo.factor,
          });
        } else {
          otherUnits.push(unit);
        }
      }
    });

    const result = { packagingUnits, otherUnits };
    if (articleId) this.groupedUnitsCache.set(articleId, result);
    return result;
  }

  async needsCustomFactor(rowIndex: number): Promise<boolean> {
    const row = this.rowsArr.at(rowIndex);
    const article = row.get('article')?.value;
    const selectedUnit = Number(row.get('unit')?.value);
    const baseUnit = article?.id_unit;

    if (!article || !baseUnit || !selectedUnit || selectedUnit === baseUnit)
      return false;

    return !(await this.unitConversionService.hasConversion(
      selectedUnit,
      baseUnit,
    ));
  }

  // Display / Filter
  displayFnArticle(article: IArticles) {
    return article
      ? `${article.descripcion} - (${article?.tax?.[0]?.value})`
      : '';
  }

  displayFnProvider(provider: IProvider) {
    return provider?.nombre;
  }

  displayFnCity(city: ICities) {
    return city?.name;
  }

  _filterC(value: string): ICities[] {
    const filterValue = value.toString().toLowerCase();
    return this.citiesList.filter((option) =>
      option.name.toLowerCase().includes(filterValue),
    );
  }

  // Template helpers
  textValid(text: string): boolean {
    return !!(
      this.purchaseForm.get(text)?.invalid &&
      this.purchaseForm.get(text)?.touched
    );
  }

  arrayvalid(text: string): boolean {
    return !!(
      this.rowsArr.get(text)?.invalid && this.rowsArr.get(text)?.touched
    );
  }

  taxValid(tax: boolean): boolean {
    return tax === true;
  }

  closeDialog(): void {
    this.dialog.closeAll();
  }

  // Private utils
  private isSearchableValue(value: any): boolean {
    return typeof value === 'string' || typeof value === 'number';
  }
}
